import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Loader2 } from "lucide-react";
import { api, mediaUrl, formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Field } from "@/admin/fields";

export const ResourceManager = ({ title, subtitle, collection, fields, imageKey, primaryKey = "name", secondaryKey }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/content/${collection}`); setItems(data); }
    catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [collection]);

  const blank = () => { const o = {}; fields.forEach((f) => { o[f.key] = f.type === "switch" ? (f.default ?? true) : f.type === "list" ? [] : ""; }); return o; };

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/content/${collection}/${editing.id}`, editing);
      else await api.post(`/admin/content/${collection}`, editing);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/admin/content/${collection}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const reorder = async (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= items.length) return;
    const arr = [...items];
    [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
    setItems(arr);
    await api.post(`/admin/content/${collection}/reorder`, { order: arr.map((x) => x.id) });
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold uppercase text-white">{title}</h1>
          {subtitle && <p className="text-sm text-slate-400">{subtitle}</p>}
        </div>
        <button onClick={() => setEditing(blank())} data-testid={`${collection}-add-button`} className="inline-flex items-center gap-2 rounded-lg bg-[#E8A317] px-4 py-2 text-sm font-bold uppercase text-[#0A1626] hover:bg-[#CC8F12]">
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>

      {loading ? <div className="py-20 text-center"><Loader2 className="mx-auto h-7 w-7 animate-spin text-slate-400" /></div> : (
        <div className="mt-6 overflow-hidden rounded-xl border border-slate-700">
          <table className="w-full text-left text-sm">
            <thead className="bg-[#0C131F] text-xs uppercase text-slate-400">
              <tr><th className="p-3">Item</th><th className="p-3 hidden sm:table-cell">Details</th><th className="p-3 w-32">Order</th><th className="p-3 w-28 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={it.id} className="border-t border-slate-700/60 hover:bg-white/5" data-testid={`${collection}-row-${it.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      {imageKey && (
                        <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-slate-700">
                          {it[imageKey] ? <img src={mediaUrl(it[imageKey])} alt="" className="h-full w-full object-cover" /> : null}
                        </div>
                      )}
                      <div>
                        <div className="font-semibold text-white">{it[primaryKey] || "—"}</div>
                        {it.migration_status && it.migration_status !== "verified" && <span className="text-[10px] font-bold uppercase text-amber-400">Awaiting verification</span>}
                      </div>
                    </div>
                  </td>
                  <td className="p-3 hidden sm:table-cell text-slate-400">{secondaryKey ? it[secondaryKey] : ""}{typeof it.active === "boolean" && <span className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-bold ${it.active ? "bg-green-900/40 text-green-400" : "bg-slate-700 text-slate-400"}`}>{it.active ? "ACTIVE" : "HIDDEN"}</span>}{typeof it.published === "boolean" && <span className={`ml-1 rounded px-1.5 py-0.5 text-[10px] font-bold ${it.published ? "bg-blue-900/40 text-blue-400" : "bg-slate-700 text-slate-400"}`}>{it.published ? "PUBLISHED" : "DRAFT"}</span>}{typeof it.approved === "boolean" && <span className={`ml-1 rounded px-1.5 py-0.5 text-[10px] font-bold ${it.approved ? "bg-green-900/40 text-green-400" : "bg-slate-700 text-slate-400"}`}>{it.approved ? "APPROVED" : "PENDING"}</span>}</td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <button onClick={() => reorder(idx, -1)} className="rounded bg-slate-700 p-1 text-slate-300 hover:bg-slate-600"><ArrowUp className="h-3.5 w-3.5" /></button>
                      <button onClick={() => reorder(idx, 1)} className="rounded bg-slate-700 p-1 text-slate-300 hover:bg-slate-600"><ArrowDown className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setEditing(it)} data-testid={`${collection}-edit-${it.id}`} className="rounded bg-slate-700 p-1.5 text-slate-200 hover:bg-[#1E63D6]"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => remove(it.id)} data-testid={`${collection}-delete-${it.id}`} className="rounded bg-slate-700 p-1.5 text-slate-200 hover:bg-red-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-slate-500">No items yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto border-slate-700 bg-[#151F30] text-white">
          <DialogHeader><DialogTitle className="font-heading uppercase">{editing?.id ? "Edit" : "Add"} {title}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid gap-4 sm:grid-cols-2">
              {fields.map((f) => (
                <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
                  <Field field={f} value={editing[f.key]} onChange={(v) => setEditing({ ...editing, [f.key]: v })} />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="rounded-lg border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-300">Cancel</button>
            <button onClick={save} disabled={saving} data-testid={`${collection}-save-button`} className="inline-flex items-center gap-2 rounded-lg bg-[#E8A317] px-5 py-2 text-sm font-bold uppercase text-[#0A1626] hover:bg-[#CC8F12] disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
